import * as q from "../../queries.ts";
import { formatError, RpcClient, XrpcError } from "../api.ts";
import { authState, viewTransport } from "../auth.ts";
import { ownRecords, setRelation } from "../channels.ts";
import { ChannelMenu } from "../components/channels.tsx";
import { Feed } from "../components/feed.tsx";
import { PagedList } from "../components/paged-list.tsx";
import { PostCard } from "../components/post.tsx";
import { dropdown } from "../components/menu.tsx";
import { moderationItems } from "../components/moderation.tsx";
import { ProfileEditor } from "../components/profile-editor.tsx";
import { actorHandle, actorLabel, Avatar, initial } from "../components/actor.tsx";
import { RichText, safeExternalUrl } from "../components/rich-text.tsx";
import { isFiltered } from "../filters.ts";
import { rememberProfile } from "../own-profile.ts";
import { cachedActor, cachedProfile } from "../view-cache.ts";
import { canWriteCollection } from "../permissions.ts";
import { profilePath } from "../router.ts";
import { Follow } from "../../structs.ts";
import { profileTitle } from "../../titles.ts";

type Tab = "posts" | "replies" | "media" | "likes";
const tabs: readonly { key: Tab; label: string }[] = [
  { key: "posts", label: "Posts" },
  { key: "replies", label: "Replies" },
  { key: "media", label: "Media" },
  { key: "likes", label: "Likes" },
];

export function ProfileView(actor: string, signal: AbortSignal): Element {
  const main = <main class="profile-page"></main>;
  const profile = (
    <section class="profile-summary">
      <p class="status">Loading profile…</p>
    </section>
  );
  const rows = <div></div>;
  let page = new AbortController();
  let tab: Tab = "posts";
  let channel: string | undefined = "default";
  const reload = () => {
    page.abort();
    page = new AbortController();
    const pageSignal = AbortSignal.any([signal, page.signal]);
    const filter = tab;
    const showMuted = filter !== "likes";
    rows.replaceChildren(
      filter === "likes"
        ? PagedList(
          async (cursor) => {
            const result = await new RpcClient(viewTransport(q.getActorLikes.id)).call(
              q.getActorLikes,
              {
                parameters: { actor, limit: 30, ...(cursor && { cursor }) },
                signal: pageSignal,
              },
            );
            return { items: result.posts, ...(result.cursor && { cursor: result.cursor }) };
          },
          (post) => post.uri,
          (post) =>
            isFiltered(post) ? undefined : PostCard({ post, signal: pageSignal, showMuted }),
          pageSignal,
          "No likes yet.",
        )
        : Feed(
          (cursor) =>
            new RpcClient(viewTransport(q.getActorFeed.id)).call(q.getActorFeed, {
              parameters: {
                actor,
                limit: 30,
                filter,
                ...(channel && { channel }),
                ...(cursor && { cursor }),
              },
              signal: pageSignal,
            }),
          pageSignal,
          showMuted,
          filter === "posts" ? "threads" : filter === "replies" ? "parents" : undefined,
        ),
    );
  };
  const tabStrip = <div class="profile-tabs"></div>;
  const tabRow = (
    <nav class="profile-tabrow" aria-label="Profile content">
      {tabStrip}
    </nav>
  );
  for (const entry of tabs) {
    const button = (
      <button
        class="profile-tab"
        type="button"
        aria-current={entry.key === tab ? "page" : undefined}
      >
        {entry.label}
      </button>
    ) as HTMLButtonElement;
    button.addEventListener("click", () => {
      tab = entry.key;
      for (const other of tabStrip.querySelectorAll("button")) {
        other.removeAttribute("aria-current");
      }
      button.setAttribute("aria-current", "page");
      const channels = tabRow.querySelector<HTMLButtonElement>(".channel-menu-button");
      if (channels) {
        channels.disabled = tab === "likes";
        channels.title = tab === "likes" ? "Likes are not filtered by channel" : "";
      }
      reload();
    });
    tabStrip.append(button);
  }
  reload();
  main.append(profile, <section class="feed-section" aria-label="Posts">{rows}</section>);

  void loadProfile(
    new RpcClient(viewTransport(q.getProfile.id)),
    actor,
    profile,
    tabRow,
    (next) => {
      channel = next;
      reload();
    },
    signal,
  );
  return main;
}

async function loadProfile(
  client: RpcClient,
  actor: string,
  target: Element,
  tabRow: Element,
  selectChannel: (channel?: string) => void,
  signal: AbortSignal,
): Promise<void> {
  let shown: AbortController | undefined;
  let channelMenu: Element | undefined;
  const show = (value: q.ProfileView) => {
    shown?.abort();
    shown = new AbortController();
    document.title = profileTitle(value.actor);
    // The menu keeps the reader's channel choice, so a fresher view mustn't rebuild it.
    channelMenu ??= tabRow.appendChild(
      ChannelMenu(value.actor.did, value.channels, selectChannel, signal),
    );
    const { identity, edit, body, viewer } = profileSummary(
      value,
      AbortSignal.any([signal, shown.signal]),
    );
    target.replaceChildren(identity, edit, body, tabRow, viewer);
  };

  // Draw what earlier pages already said about this account, then replace it with the full view.
  const cached = cachedProfile(actor);
  const known = cachedActor(actor);
  if (cached) show(cached);
  else if (known) {
    document.title = profileTitle(known);
    target.replaceChildren(
      <div class="profile-identity">
        {Avatar(known)}
        <span class="actor-identity">
          <strong>{actorLabel(known)}</strong>
          <span>{actorHandle(known)}</span>
        </span>
      </div>,
    );
  }

  try {
    let value: q.ProfileView;
    for (let attempt = 0;; attempt++) {
      try {
        value = await client.call(q.getProfile, { parameters: { actor }, signal });
        break;
      } catch (error) {
        const state = authState.get();
        const retryingOwnProfile = state.status === "loading" ||
          state.status === "authenticated" && state.did === actor;
        if (
          attempt === 9 || !(error instanceof XrpcError) || error.error !== "ActorNotFound" ||
          !retryingOwnProfile
        ) throw error;
        target.replaceChildren(<p class="status">Preparing your profile…</p>);
        await new Promise((resolve) => setTimeout(resolve, 1000));
        if (signal.aborted) return;
      }
    }
    rememberProfile(value);
    if (!signal.aborted) show(value);
  } catch (error) {
    if (!signal.aborted) target.replaceChildren(errorView("Could not load this profile", error));
  }
}

function profileSummary(value: q.ProfileView, signal: AbortSignal) {
  const identity = <div class="profile-identity"></div>;
  const body = <div class="profile-body"></div>;
  const edit = <div class="profile-edit"></div>;
  const menuTrigger = (
    <button class="icon-button" type="button" aria-label="Profile options">
      <span class="lucide lucide-more" aria-hidden="true"></span>
    </button>
  ) as HTMLButtonElement;
  const menu = <div class="menu menu-right" role="menu"></div> as HTMLElement;
  menuTrigger.addEventListener(
    "click",
    () => menu.replaceChildren(...moderationItems(value.actor)),
  );
  dropdown(menuTrigger, menu, signal);
  let avatarPreview: string | undefined;
  let currentRecord = value.record;
  const viewerImage = <img class="avatar-viewer-image" alt="" /> as HTMLImageElement;
  const viewer = (
    <dialog class="avatar-viewer" aria-label="Profile picture">
      {viewerImage}
      <button class="avatar-viewer-close" type="button" aria-label="Close">
        ×
      </button>
    </dialog>
  ) as HTMLDialogElement;
  viewer.querySelector("button")!.addEventListener("click", () => viewer.close());
  viewer.addEventListener("click", (event) => {
    if (event.target === viewer) viewer.close();
  });
  const render = (record: q.ProfileView["record"]) => {
    const displayName = record?.displayName || value.actor.displayName;
    const label = displayName || value.actor.handle || value.actor.did;
    const avatar = avatarPreview ?? value.actor.avatar;
    const avatarUrl = (avatar && safeExternalUrl(avatar)) || undefined;
    viewerImage.src = avatarUrl ?? "";
    viewerImage.alt = avatarUrl ? `Profile picture of ${label}` : "";
    const face = avatarUrl
      ? <img class="avatar" src={avatarUrl} alt="" />
      : <span class="avatar" aria-hidden="true">{initial(label)}</span>;
    identity.replaceChildren(
      avatarUrl
        ? (
          <button
            class="avatar-button"
            type="button"
            aria-label={`View profile picture of ${label}`}
            _onclick={() => viewer.showModal()}
          >
            {face}
          </button>
        )
        : face,
      <span class="actor-identity">
        <strong>{label}</strong>
        <span>{actorHandle(value.actor)}</span>
      </span>,
    );
    body.replaceChildren();
    const relationship = value.actor.viewer;
    if (relationship?.blockedBy || relationship?.blocking) {
      const name = actorHandle(value.actor);
      body.append(
        <p class="profile-notice">
          {relationship.blockedBy
            ? `${name} is blocking you. You can't see their posts.`
            : "You blocked this account."}
        </p>,
      );
    }
    const path = profilePath(value.actor.handle ?? value.actor.did);
    body.append(
      <div class="profile-counts">
        <a href={`${path}/followers`} dataset={{ route: "" }}>
          <strong>{value.followersCount.toLocaleString()}</strong> Followers
        </a>
        <a href={`${path}/following`} dataset={{ route: "" }}>
          <strong>{value.followingCount.toLocaleString()}</strong> Following
        </a>
        <span>
          <strong>{value.postsCount.toLocaleString()}</strong> Posts
        </span>
      </div>,
    );
    if (record?.description) {
      const description = <p class="profile-description"></p>;
      description.append(
        RichText({ text: record.description, facets: record.descriptionFacets ?? [] }),
      );
      body.append(description);
    }
    if (record?.website) {
      const href = safeExternalUrl(record.website);
      if (href) {
        body.append(
          <p>
            <a href={href} target="_blank" rel="noopener noreferrer">
              {new URL(href).hostname}
            </a>
          </p>,
        );
      }
    }
    currentRecord = record;
  };
  const renderEditor = () => {
    const state = authState.get();
    edit.replaceChildren();
    if (state.status === "authenticated" && state.did !== value.actor.did) {
      edit.append(<span class="menu-anchor">{menuTrigger}{menu}</span>);
      if (
        canWriteCollection(state.scopes, Follow.id, "create") &&
        canWriteCollection(state.scopes, Follow.id, "delete")
      ) {
        edit.append(FollowButton(state.did as `did:${string}`, value.actor.did, signal));
      }
    }
    if (state.status === "authenticated" && state.did === value.actor.did) {
      edit.append(
        ProfileEditor(
          value.actor.did,
          currentRecord,
          (updated, preview) => {
            if (avatarPreview) URL.revokeObjectURL(avatarPreview);
            avatarPreview = preview;
            render(updated);
            renderEditor();
          },
          signal,
        ),
      );
    }
  };
  render(value.record);
  const subscription = authState.subscribeImmediate(renderEditor);
  signal.addEventListener("abort", () => {
    subscription.unsubscribe();
    if (avatarPreview) URL.revokeObjectURL(avatarPreview);
  }, { once: true });
  return { identity, edit, body, viewer };
}

function FollowButton(
  viewer: `did:${string}`,
  subject: `did:${string}`,
  signal: AbortSignal,
): Element {
  const button = <button type="button" disabled>…</button> as HTMLButtonElement;
  const status = <span class="status" role="status"></span>;
  let following = false;
  let busy = true;
  const paint = () => {
    button.textContent = following ? "Unfollow" : "Follow";
    button.disabled = busy;
  };
  const refresh = () => {
    busy = true;
    paint();
    void ownRecords(viewer, Follow.id, signal).then((records) => {
      if (signal.aborted) return;
      following = records.some((entry) =>
        entry.value.$type === Follow.id && entry.value.subject === subject &&
        entry.value.channel === undefined
      );
      busy = false;
      paint();
    }).catch((error) => {
      if (signal.aborted) return;
      busy = false;
      paint();
      status.textContent = formatError(error);
    });
  };
  button.addEventListener("click", () => {
    if (busy) return;
    busy = true;
    paint();
    status.textContent = "";
    void setRelation(viewer, Follow.id, subject, !following, undefined, signal).then(() => {
      if (signal.aborted) return;
      following = !following;
      busy = false;
      paint();
    }).catch((error) => {
      if (signal.aborted) return;
      busy = false;
      paint();
      status.textContent = formatError(error);
    });
  });
  refresh();
  return <span class="profile-follow">{button}{status}</span>;
}

function errorView(title: string, error: unknown): Element {
  return (
    <div class="error" role="alert">
      <strong>{title}</strong>
      <span>{formatError(error)}</span>
    </div>
  );
}
